import { Divider, Group, ScrollArea, Stack } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import type { AgentFinancialResponse } from '../../../../api/events-client/events-client.type';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { FilterItem } from '../../../../components/filters/filter-item';
import { ResetFiltersButton } from '../../../../components/filters/reset-filters-button';
import { SmallSquareButton } from '../../../../components/small-square-button/small-square-button';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  resetAgentCostsFilters,
  setAgentCostsCategory,
  setAgentCostsOptions,
  toggleAgentCostsOption,
  toggleAgentCostsTrendLine,
} from '../../../../store/financials-store/financials-store.actions';
import type { AgentCostsCategory } from '../../../../store/financials-store/financials-store.type';
import { Select } from '../../../../ui-library/select/select';
import { Text } from '../../../../ui-library/typography/typography';
import { MAX_SELECTED_FILTERS } from '../../constants';
import { buildDisplayKeyMap, getCategoryItems } from './agent-costs.helpers';

type FiltersProps = {
  setShow: (show: boolean) => void;
  data?: AgentFinancialResponse;
  isPending: boolean;
};

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'users', label: 'Users' },
  { value: 'epics', label: 'Epics' },
  { value: 'initiatives', label: 'Initiatives' },
];

function Filters({ setShow, data, isPending }: FiltersProps) {
  const { selectedCategory, selectedOptions, selectedTrendLines } = useFinancialsStore(
    (state) => state.filters.agentCosts,
  );

  const categoryItems = useMemo(() => {
    if (!data) return {};
    const items = getCategoryItems(data, selectedCategory);
    const keyMap = buildDisplayKeyMap(items);
    return Object.fromEntries(items.map((item) => [keyMap[item.id], keyMap[item.id]]));
  }, [data, selectedCategory]);

  // Auto-select top 4 items when category changes or data loads
  useEffect(() => {
    const itemKeys = Object.keys(categoryItems);
    if (itemKeys.length > 0 && selectedOptions.length === 0) {
      const topFour = itemKeys.slice(0, 4);
      setAgentCostsOptions(topFour);
    }
  }, [categoryItems, selectedOptions.length]);

  return (
    <Stack h="100%" style={{ position: 'relative' }}>
      <Group>
        <span style={{ flex: 1 }}>
          <Select
            size="xs"
            value={selectedCategory}
            options={CATEGORY_OPTIONS}
            placeholder="Select Category"
            onChange={(value) => setAgentCostsCategory(value as AgentCostsCategory)}
          />
        </span>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        <Stack pb={40}>
          <Group justify="space-between">
            <Text size="small" weight="bold">
              {CATEGORY_OPTIONS.find((option) => option.value === selectedCategory)?.label}
            </Text>
            <Text size="small" weight="bold">
              Trends
            </Text>
          </Group>
          {!isPending && Object.keys(categoryItems).length > 0
            ? Object.entries(categoryItems).map(([id, name]) => (
                <FilterItem
                  key={id}
                  value={id}
                  label={name}
                  isSelected={selectedOptions.includes(id)}
                  isSelectedTrends={selectedTrendLines.includes(id)}
                  showTrends={true}
                  selectedCount={selectedOptions.length}
                  maxSelected={MAX_SELECTED_FILTERS}
                  onToggle={toggleAgentCostsOption}
                  onTrendsToggle={toggleAgentCostsTrendLine}
                />
              ))
            : !isPending && <ContentPlaceholder icon="event_busy" text="No filtered data" />}
          <Divider />
        </Stack>
      </ScrollArea>
      <ResetFiltersButton
        onClick={() => resetAgentCostsFilters()}
        visible={selectedOptions.length > 0 || selectedTrendLines.length > 0}
      />
    </Stack>
  );
}

export { Filters };
